import { useState } from "react";
import { SlideOver } from "@/components/ui/slide-over";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Notify } from "@/components/ui/notify";
import { formatDisplay } from "@/lib/datetime-format";
import type { AppliedScenario } from "@/types/applied-scenario.types";
import { useCancelAppliedScenario } from "./hooks/useAppliedScenario";

interface CancelAppliedScenarioDialogProps {
  scenario: AppliedScenario | null;
  onOpenChange: (open: boolean) => void;
}

// Cancelar é a ÚNICA saída de um compromisso ativo, e ela não apaga
// nada: o registro fica, com o motivo. Por isso o motivo é obrigatório aqui, e por isso a
// confirmação irreversível (`ConfirmDialog`) fica entre o clique e a escrita.

export function CancelAppliedScenarioDialog({
  scenario,
  onOpenChange,
}: CancelAppliedScenarioDialogProps) {
  const cancel = useCancelAppliedScenario();
  const [reason, setReason] = useState("");

  async function submit() {
    if (!scenario || reason.trim().length === 0) return;
    const ok = await Notify.confirm({
      variant: "danger",
      title: "Cancelar este compromisso?",
      description:
        "A vigência deixa de valer e a janela fica livre para outro cenário. O registro permanece no histórico, com o motivo — não há como apagá-lo depois.",
      confirmLabel: "Cancelar compromisso",
      cancelLabel: "Voltar",
    });
    if (!ok) return;
    cancel.mutate(
      { id: scenario.id, lineId: scenario.lineId, reason: reason.trim() },
      {
        onSuccess: () => {
          setReason("");
          onOpenChange(false);
        },
      }
    );
  }

  return (
    <SlideOver
      open={!!scenario}
      onOpenChange={onOpenChange}
      title="Cancelar compromisso"
      width={420}
    >
      <div className="space-y-4 p-5">
        {scenario ? (
          <p className="text-[13px] text-text-secondary">
            Vigência de {formatDisplay(scenario.windowStart)} a {formatDisplay(scenario.windowEnd)}.
          </p>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="cancel-reason">Motivo do cancelamento</Label>
          <Input
            id="cancel-reason"
            value={reason}
            maxLength={500}
            placeholder="Ex.: a OP saiu da linha antes do início da vigência"
            onChange={(e) => setReason(e.target.value)}
          />
          <p className="text-[12px] text-text-secondary">
            O motivo entra no histórico da decisão — é o que explica, meses depois, por que esta
            janela não aconteceu.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length === 0 || cancel.isPending}
            onClick={() => void submit()}
          >
            {cancel.isPending ? "Cancelando…" : "Cancelar compromisso"}
          </Button>
        </div>
      </div>
    </SlideOver>
  );
}
